import type { ElplanInfoTagForDate } from 'api/elplan/elplan.types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { findTagById, getBidTagSelectionOptions } from '../bidTagSelection.helper'
import type { BidProductionConsumption, BidTagSelectionGroup } from '../DayAheadBidEditor.types'

type UseBidTagSelectionParams = {
  tagsForDate: ElplanInfoTagForDate[]
  requireTagForBlockBids: boolean
  canSelectTags: boolean
  initialTagId: number | null
}

export type UseBidTagSelectionResult = {
  selectedTagId: number | null
  effectiveProductionConsumption: BidProductionConsumption
  groupedTagOptions: BidTagSelectionGroup[]
  hasAvailableTagsForSelectedType: boolean
  tagValidationError: string | undefined
  tagToSubmit: number | null
  handleProductionConsumptionChange: (next: BidProductionConsumption) => void
  handleTagChange: (value: string) => void
  validateTagBeforeSubmit: () => boolean
}

export function useBidTagSelection({
  tagsForDate,
  requireTagForBlockBids,
  canSelectTags,
  initialTagId,
}: UseBidTagSelectionParams): UseBidTagSelectionResult {
  const { t } = useTranslation()
  const [selectedTagId, setSelectedTagId] = useState<number | null>(initialTagId)
  const [selectedProductionConsumption, setSelectedProductionConsumption] = useState<BidProductionConsumption>(
    () => findTagById(tagsForDate, initialTagId)?.production_consumption ?? 'production'
  )
  const [tagValidationError, setTagValidationError] = useState<string>()

  const effectiveProductionConsumption =
    findTagById(tagsForDate, selectedTagId)?.production_consumption ?? selectedProductionConsumption
  const groupedTagOptions = getBidTagSelectionOptions(tagsForDate, effectiveProductionConsumption)
  const hasAvailableTagsForSelectedType = groupedTagOptions.some((group) =>
    group.options.some((option) => !option.disabled)
  )
  const tagToSubmit = requireTagForBlockBids ? selectedTagId : null

  function handleProductionConsumptionChange(nextSelection: BidProductionConsumption): void {
    setSelectedProductionConsumption(nextSelection)
    setTagValidationError(undefined)

    const availableTagIds = getBidTagSelectionOptions(tagsForDate, nextSelection)
      .flatMap((group) => group.options)
      .filter((option) => !option.disabled)
      .map((option) => option.id)

    if (selectedTagId && !availableTagIds.includes(selectedTagId)) {
      setSelectedTagId(null)
    }
  }

  function handleTagChange(value: string): void {
    setTagValidationError(undefined)
    setSelectedTagId(value === '' ? null : Number(value))
  }

  function validateTagBeforeSubmit(): boolean {
    if (!canSelectTags || !requireTagForBlockBids) {
      setTagValidationError(undefined)
      return true
    }

    if (!selectedTagId) {
      setTagValidationError(t('Tag is required'))
      return false
    }

    if (!findTagById(tagsForDate, selectedTagId)) {
      setTagValidationError(t('Selected tag is no longer available for the selected date. Please reselect.'))
      return false
    }

    setTagValidationError(undefined)
    return true
  }

  return {
    selectedTagId,
    effectiveProductionConsumption,
    groupedTagOptions,
    hasAvailableTagsForSelectedType,
    tagValidationError,
    tagToSubmit,
    handleProductionConsumptionChange,
    handleTagChange,
    validateTagBeforeSubmit,
  }
}
